<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import GbDialog from '@/components/GbDialog.vue'
import { usePresentLevelsStore } from '@/presentlevels/presentlevels.store'
import type { SubjectOption } from '@/presentlevels/presentlevels_types'

interface Props {
  visible: boolean
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'add-subject', subjectUUIDs: string[], subjectLabel: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const presentLevelsStore = usePresentLevelsStore()
const { subjectOptions: availableSubjectOptions } = storeToRefs(presentLevelsStore)

const closeModal = () => {
  emit('update:visible', false)
}

// Automatically add subject when selected
const onSubjectSelect = (subject: SubjectOption) => {
  emit('add-subject', [subject.uuid], subject.label)
  closeModal()
}
</script>

<template>
  <GbDialog 
    dialogId="add-subject-modal"
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    header="Add Present Level Area"
  >
    <template #bodyContent>
      <div class="pb-6 px-6">
      <div v-if="presentLevelsStore.loadingSubjects" class="text-gray-500 text-center py-8">
        Loading subjects...
      </div>
      
      <div v-else-if="availableSubjectOptions.length === 0" class="text-gray-500 italic text-center py-8">
        No subjects available.
      </div>
      
      <div v-else class="flex flex-wrap gap-4">
        <button 
          v-for="subject in availableSubjectOptions" 
          :key="subject.uuid"
          class="bg-blue-200 hover:bg-blue-300 rounded-lg px-4 py-2 cursor-pointer transition-colors duration-200 flex items-center justify-start w-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          @click="onSubjectSelect(subject)"
          :aria-label="`Add ${subject.label} present level area`"
          data-testid="subject-option"
        >
          <div class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span class="material-icons-round text-green-300 text-xl" aria-hidden="true">add_circle</span>
          </div>
          <span class="text-base whitespace-nowrap">
            {{ subject.label }}
          </span>
        </button>
      </div>
      </div>
    </template>
  </GbDialog>
</template>
